import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, ArrowRight, Package, Users, Star } from "lucide-react";
import { getIcon } from "../utils/icons";
import api from "../lib/api";

/**
 * MegaMenu - Full-width dropdown mega menu.
 * Two display modes per category:
 *  1. "icon_grid" — subcategories as icon/image cards + optional brands/products panels
 *  2. "list" — classic wide layout: subcategory list + grandchildren + brands + products
 *
 * Mode source: per-category config → global navSettings.display_mode → auto-detect
 */
export default function MegaMenu({ categoryTree, navSettings, greenH, iconSize, fontSize }) {
    const [openCat, setOpenCat] = useState(null);
    const [hoveredSub, setHoveredSub] = useState(null);
    const [catProducts, setCatProducts] = useState({});
    const [catBrands, setCatBrands] = useState({});
    const closeTimer = useRef(null);

    const rawRoots = (categoryTree || []).filter(c => !c.parent_id).slice(0, 10);
    const showProducts = navSettings?.mega_menu_show_products !== false;
    const showBrandsGlobal = navSettings?.mega_menu_show_brands !== false;
    const productsCount = navSettings?.mega_menu_products_count || 4;
    const brandIconScale = navSettings?.mega_menu_brand_icon_scale || 2;
    const globalDisplayMode = navSettings?.display_mode || "auto";
    const catConfigs = navSettings?.mega_menu_category_config || [];
    const getCatCfg = (catId) => catConfigs.find(c => c.category_id === catId) || {};

    const rootCats = rawRoots
        .filter(cat => getCatCfg(cat.id).visible !== false)
        .sort((a, b) => (getCatCfg(a.id).sort_order ?? 999) - (getCatCfg(b.id).sort_order ?? 999));

    const fetchProducts = useCallback(async (catId) => {
        if (!showProducts || catProducts[catId]) return;
        try {
            const { data } = await api.get(`/products?category_id=${catId}&limit=${productsCount}&sort=bestselling`);
            setCatProducts(prev => ({ ...prev, [catId]: (data.items || data || []).slice(0, productsCount) }));
        } catch {}
    }, [catProducts, showProducts, productsCount]);

    const fetchBrands = useCallback(async (catId) => {
        if (catBrands[catId]) return;
        try {
            const { data } = await api.get(`/categories/${catId}/brands`);
            setCatBrands(prev => ({ ...prev, [catId]: (data || []).slice(0, 8) }));
        } catch {}
    }, [catBrands]);

    const handleOpen = (cat) => {
        clearTimeout(closeTimer.current);
        setOpenCat(cat);
        setHoveredSub(null);
        if (showProducts) fetchProducts(cat.id);
        fetchBrands(cat.id);
    };

    const handleClose = () => {
        closeTimer.current = setTimeout(() => { setOpenCat(null); setHoveredSub(null); }, 180);
    };

    const cancelClose = () => clearTimeout(closeTimer.current);

    const onSubHover = (ch) => {
        setHoveredSub(ch);
        if (showProducts) fetchProducts(ch.id);
        fetchBrands(ch.id);
    };

    // Determine display mode: per-category config → global → auto
    const catCfg = openCat ? getCatCfg(openCat.id) : {};
    const catChildren = openCat?.children || [];
    let displayMode = catCfg.display_mode || globalDisplayMode || "auto";
    if (displayMode === "auto") {
        displayMode = catChildren.length > 20 ? "list" : "icon_grid";
    }
    const isProductsGrid = displayMode === "products_grid";

    return (
        <nav className="hidden md:block relative" style={{ background: "var(--sg-primary)", height: `${greenH}px`, zIndex: 150 }}
            data-testid="main-nav">
            <div className="sg-container flex items-center h-full" style={{ paddingTop: 0, paddingBottom: 0, overflowX: "visible", flexWrap: "nowrap" }}>
                {rootCats.map(cat => {
                    const isOpen = openCat?.id === cat.id;
                    const hasChildren = cat.children && cat.children.length > 0;
                    const catCfgLocal = getCatCfg(cat.id);
                    const isProductMode = catCfgLocal.display_mode === "products_grid";
                    const customIcon = catCfgLocal.icon || cat.icon;
                    const IconComp = getIcon(customIcon);
                    const displayName = catCfgLocal.custom_label || cat.name;
                    const customImg = catCfgLocal.custom_image_url || cat.image_url;
                    return (
                        <div key={cat.id} className="relative" style={{ flexShrink: 1, minWidth: 0 }}
                            onMouseEnter={() => handleOpen(cat)}
                            onMouseLeave={handleClose}>
                            <Link
                                to={`/kategorie/${cat.slug}`}
                                className="flex items-center gap-1 text-white font-semibold whitespace-nowrap transition-colors hover:bg-white/15"
                                style={{
                                    fontSize: `clamp(11px, ${fontSize}px, ${fontSize}px)`,
                                    padding: `0 clamp(6px, 1vw, ${fontSize >= 14 ? 14 : 10}px)`,
                                    height: `${greenH}px`,
                                    lineHeight: `${greenH}px`,
                                    letterSpacing: "0.2px",
                                    background: isOpen ? "rgba(0,0,0,0.18)" : "transparent",
                                }}
                                data-testid={`topcatnav-${cat.slug}`}
                                onClick={() => setOpenCat(null)}
                            >
                                {customImg ? (
                                    <img src={customImg} alt="" style={{ width: iconSize, height: iconSize }} className="object-contain" />
                                ) : (
                                    <IconComp size={iconSize} />
                                )}
                                <span>{displayName}</span>
                                {(hasChildren || isProductMode) && <ChevronDown size={10} className="opacity-60 ml-0.5" />}
                            </Link>
                        </div>
                    );
                })}
            </div>

            {/* Mega Menu Dropdown */}
            {openCat && (catChildren.length > 0 || isProductsGrid) && (
                isProductsGrid ? (
                    <ProductsGridDropdown
                        openCat={openCat}
                        setOpenCat={setOpenCat}
                        products={catProducts[openCat?.id] || []}
                        brands={showBrandsGlobal && catCfg.show_brands !== false ? (catBrands[openCat?.id] || []) : []}
                        showBrands={showBrandsGlobal && catCfg.show_brands !== false}
                        cancelClose={cancelClose}
                        handleClose={handleClose}
                        productsCount={productsCount}
                        brandIconScale={brandIconScale}
                    />
                ) : displayMode === "icon_grid" ? (
                    <IconGridDropdown
                        openCat={openCat}
                        setOpenCat={setOpenCat}
                        catChildren={catChildren}
                        cancelClose={cancelClose}
                        handleClose={handleClose}
                        brands={showBrandsGlobal ? (catBrands[openCat?.id] || []) : []}
                        products={showProducts ? (catProducts[openCat?.id] || []) : []}
                        showBrands={showBrandsGlobal && catCfg.show_brands !== false}
                        showProducts={showProducts && catCfg.show_products !== false}
                        brandIconScale={brandIconScale}
                    />
                ) : (
                    <ListDropdown
                        openCat={openCat}
                        setOpenCat={setOpenCat}
                        catChildren={catChildren}
                        hoveredSub={hoveredSub}
                        onSubHover={onSubHover}
                        subGrandchildren={hoveredSub?.children || []}
                        shouldShowGrandchildren={catCfg.show_grandchildren !== false}
                        shouldShowBrands={showBrandsGlobal && catCfg.show_brands !== false}
                        brandIconScale={brandIconScale}
                        products={(showProducts && catCfg.show_products !== false) ? (catProducts[hoveredSub?.id || openCat?.id] || []) : []}
                        brands={(showBrandsGlobal && catCfg.show_brands !== false) ? (catBrands[hoveredSub?.id || openCat?.id] || catBrands[openCat?.id] || []) : []}
                        cancelClose={cancelClose}
                        handleClose={handleClose}
                    />
                )
            )}
        </nav>
    );
}


/* ═══════════════════════════════════════════════════════════════════
   PRODUCTS GRID DROPDOWN — shows products directly instead of subcategories
   ═══════════════════════════════════════════════════════════════════ */
function ProductsGridDropdown({ openCat, setOpenCat, products, brands, showBrands, cancelClose, handleClose, productsCount, brandIconScale = 2 }) {
    const resolveImg = (url) => {
        if (!url) return null;
        if (url.startsWith("http")) return url;
        if (url.startsWith("/p/")) return url;
        return url;
    };

    return (
        <div className="absolute left-0 right-0 top-full bg-white border-b-2 border-green-500 z-[300]"
            style={{ boxShadow: "0 16px 48px rgba(0,0,0,0.12)" }}
            onMouseEnter={cancelClose}
            onMouseLeave={handleClose}
            data-testid={`megamenu-${openCat.slug}`}>
            <div className="sg-container py-5">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-bold text-gray-900">{openCat.name}</h3>
                    <Link to={`/kategorie/${openCat.slug}`}
                        className="text-xs font-semibold text-green-600 hover:text-green-700 flex items-center gap-1"
                        onClick={() => setOpenCat(null)}>
                        Zobrazit vse <ArrowRight size={12} />
                    </Link>
                </div>

                {/* Product grid */}
                {products.length > 0 ? (
                    <div className="grid gap-2.5" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(180px, 1fr))`, maxWidth: products.length <= 4 ? `${products.length * 200}px` : undefined }}>
                        {products.slice(0, 6).map(p => {
                            const img = resolveImg(p.main_image);
                            return (
                                <Link key={p.id} to={`/produkt/${p.slug}`}
                                    className="group bg-gray-50 rounded-lg border border-gray-100 hover:border-green-200 hover:shadow-md transition-all overflow-hidden"
                                    onClick={() => setOpenCat(null)}
                                    data-testid={`megamenu-pgrid-${p.slug}`}>
                                    <div className="aspect-[4/3] bg-white flex items-center justify-center p-2 overflow-hidden">
                                        {img ? (
                                            <img src={img} alt={p.name}
                                                className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-200"
                                                loading="lazy" />
                                        ) : (
                                            <Package size={28} className="text-gray-200" />
                                        )}
                                    </div>
                                    <div className="px-2.5 pb-2.5 pt-1.5">
                                        <p className="text-[11px] font-medium text-gray-600 group-hover:text-green-700 line-clamp-2 leading-tight transition-colors">
                                            {p.name}
                                        </p>
                                        <p className="font-bold mt-1 text-xs" style={{ color: "var(--sg-primary)" }}>
                                            {(p.sale_price || p.price || 0).toLocaleString("cs-CZ")} Kč
                                            {p.sale_price && p.price > p.sale_price && (
                                                <span className="text-gray-400 line-through font-normal ml-1 text-[10px]">
                                                    {p.price.toLocaleString("cs-CZ")} Kč
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                ) : (
                    <div className="flex items-center justify-center py-8 text-gray-300 text-sm">
                        <Package size={20} className="mr-2 opacity-40" /> Nacitam produkty...
                    </div>
                )}

                {/* Brands bar */}
                {showBrands && brands.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-gray-100">
                        <p className="text-gray-400 font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ fontSize: "10px" }}>
                            <Users size={10} /> Značky
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                            {brands.map((b, idx) => {
                                const bName = typeof b === "string" ? b : (b?.name || "");
                                const bLogo = typeof b === "object" ? b?.logo_url : null;
                                if (!bName) return null;
                                return (
                                    <Link key={`${bName}-${idx}`} to={`/kategorie/${openCat.slug}?brand=${encodeURIComponent(bName)}`}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-gray-100 hover:border-green-200 hover:bg-green-50 transition-colors"
                                        onClick={() => setOpenCat(null)}
                                        data-testid={`megamenu-brand-${bName}`}>
                                        {bLogo && <img src={bLogo} alt={bName} style={{ height: `${14 * brandIconScale}px`, maxWidth: `${28 * brandIconScale}px` }} className="w-auto object-contain" />}
                                        <span className="text-gray-600 font-medium truncate" style={{ fontSize: "11px" }}>{bName}</span>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}


/* ═══════════════════════════════════════════════════════════════════
   ICON GRID DROPDOWN — subcategories as icon/image cards + brands/products
   ═══════════════════════════════════════════════════════════════════ */
function IconGridDropdown({ openCat, setOpenCat, catChildren, cancelClose, handleClose, brands, products, showBrands, showProducts, brandIconScale = 2 }) {
    const maxH = typeof window !== "undefined" ? Math.min(window.innerHeight - 140, 560) : 440;
    const colCount = Math.min(Math.max(Math.ceil(catChildren.length / 2), 5), 8);
    const hasProducts = showProducts && products.length > 0;

    return (
        <div className="absolute left-0 right-0 top-full bg-white border-b-2 border-green-500 z-[300]"
            style={{ boxShadow: "0 16px 48px rgba(0,0,0,0.12)" }}
            onMouseEnter={cancelClose}
            onMouseLeave={handleClose}
            data-testid={`megamenu-${openCat.slug}`}>
            <div className="sg-container py-5">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-bold text-gray-900">{openCat.name}</h3>
                    <Link to={`/kategorie/${openCat.slug}`}
                        className="text-xs font-semibold text-green-600 hover:text-green-700 flex items-center gap-1"
                        onClick={() => setOpenCat(null)}>
                        Zobrazit vse <ArrowRight size={12} />
                    </Link>
                </div>

                <div className="flex gap-5">
                    {/* Main: Icon grid + brands underneath */}
                    <div className={`${hasProducts ? "flex-1" : "w-full"} overflow-y-auto`} style={{ maxHeight: `${maxH - 72}px` }}>
                        <div className="grid gap-2"
                            style={{ gridTemplateColumns: `repeat(${hasProducts ? Math.min(colCount, 6) : colCount}, minmax(0, 1fr))` }}>
                            {catChildren.map(ch => {
                                const ChIcon = getIcon(ch.icon);
                                return (
                                    <Link key={ch.id} to={`/kategorie/${ch.slug}`}
                                        className="group flex flex-col items-center gap-2 p-3 rounded-xl border border-gray-100 hover:border-green-200 hover:bg-green-50/50 transition-all hover:shadow-sm"
                                        onClick={() => setOpenCat(null)}
                                        data-testid={`megamenu-icon-${ch.slug}`}>
                                        <div className="w-14 h-14 rounded-xl bg-gray-50 group-hover:bg-white flex items-center justify-center overflow-hidden transition-colors">
                                            {ch.image_url ? (
                                                <img src={ch.image_url} alt={ch.name} className="w-12 h-12 object-contain group-hover:scale-110 transition-transform" loading="lazy" />
                                            ) : (
                                                <ChIcon size={24} className="text-gray-400 group-hover:text-green-600 transition-colors" />
                                            )}
                                        </div>
                                        <span className="text-[11px] font-semibold text-gray-700 group-hover:text-green-700 text-center leading-tight line-clamp-2 transition-colors">
                                            {ch.name}
                                        </span>
                                    </Link>
                                );
                            })}
                        </div>

                        {/* Brands bar UNDER icons */}
                        {showBrands && brands.length > 0 && (
                            <div className="mt-4 pt-3 border-t border-gray-100">
                                <p className="text-gray-400 font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ fontSize: "10px" }}>
                                    <Users size={10} /> Značky
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                    {brands.map((b, idx) => {
                                        const bName = typeof b === "string" ? b : (b?.name || "");
                                        const bLogo = typeof b === "object" ? b?.logo_url : null;
                                        if (!bName) return null;
                                        return (
                                            <Link key={`${bName}-${idx}`} to={`/kategorie/${openCat.slug}?brand=${encodeURIComponent(bName)}`}
                                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-gray-100 hover:border-green-200 hover:bg-green-50 transition-colors"
                                                onClick={() => setOpenCat(null)}
                                                data-testid={`megamenu-brand-${bName}`}>
                                                {bLogo && <img src={bLogo} alt={bName} style={{ height: `${14 * brandIconScale}px`, maxWidth: `${28 * brandIconScale}px` }} className="w-auto object-contain" />}
                                                <span className="text-gray-600 font-medium truncate" style={{ fontSize: "11px" }}>{bName}</span>
                                            </Link>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* RIGHT sidebar: Top products only */}
                    {hasProducts && (
                        <div className="flex-shrink-0 border-l border-gray-100 pl-5 overflow-y-auto" style={{ width: "280px", maxHeight: `${maxH - 72}px` }}>
                            <p className="text-gray-400 font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ fontSize: "10px" }}>
                                <Star size={10} /> Top produkty
                            </p>
                            <div className="space-y-1">
                                {products.map(p => (
                                    <Link key={p.id} to={`/produkt/${p.slug}`}
                                        className="flex items-center gap-2.5 py-2 hover:bg-green-50 rounded-lg px-2 transition-colors group"
                                        onClick={() => setOpenCat(null)}
                                        data-testid={`megamenu-product-${p.slug}`}>
                                        <div className="w-12 h-12 rounded-lg bg-gray-50 border border-gray-100 flex-shrink-0 overflow-hidden flex items-center justify-center">
                                            {p.main_image ? (
                                                <img src={p.main_image} alt={p.name} className="w-full h-full object-contain p-0.5" loading="lazy" />
                                            ) : (
                                                <Package size={14} className="text-gray-300" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-gray-700 group-hover:text-green-700 font-medium leading-tight line-clamp-2" style={{ fontSize: "11px" }}>{p.name}</p>
                                            <p className="font-bold mt-0.5" style={{ fontSize: "12px", color: "var(--sg-primary)" }}>
                                                {(p.sale_price || p.price || 0).toLocaleString("cs-CZ")} Kč
                                            </p>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


/* ═══════════════════════════════════════════════════════════════════
   LIST DROPDOWN — Classic wide layout (subcategories + grandchildren + brands + products)
   ═══════════════════════════════════════════════════════════════════ */
function ListDropdown({
    openCat, setOpenCat, catChildren, hoveredSub, onSubHover,
    subGrandchildren, shouldShowGrandchildren, shouldShowBrands, brandIconScale = 2,
    products, brands, cancelClose, handleClose
}) {
    const maxH = typeof window !== "undefined" ? Math.min(window.innerHeight - 140, 520) : 420;

    return (
        <div className="absolute left-0 right-0 top-full bg-white border-b-2 border-green-500 z-[300]"
            style={{ boxShadow: "0 16px 48px rgba(0,0,0,0.12)", maxHeight: `${maxH}px` }}
            onMouseEnter={cancelClose}
            onMouseLeave={handleClose}
            data-testid={`megamenu-${openCat.slug}`}>
            <div className="sg-container py-4">
                <div className="flex gap-0" style={{ maxHeight: `${maxH - 32}px` }}>

                    {/* LEFT: Subcategories list */}
                    <div className="flex-shrink-0 border-r border-gray-100 pr-3 overflow-y-auto" style={{ width: "220px", maxHeight: `${maxH - 32}px` }}>
                        <Link to={`/kategorie/${openCat.slug}`}
                            className="flex items-center justify-between px-3 py-2 rounded-lg bg-green-50 hover:bg-green-100 transition-colors mb-2"
                            onClick={() => setOpenCat(null)}>
                            <span style={{ fontSize: "12px" }} className="font-bold text-green-700">Vse v {openCat.name}</span>
                            <ArrowRight size={12} className="text-green-500" />
                        </Link>
                        {catChildren.map(ch => {
                            const ChildIcon = getIcon(ch.icon);
                            const isActive = hoveredSub?.id === ch.id;
                            const hasGC = ch.children && ch.children.length > 0;
                            return (
                                <Link key={ch.id} to={`/kategorie/${ch.slug}`}
                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors group ${isActive ? "bg-green-50" : "hover:bg-gray-50"}`}
                                    onMouseEnter={() => onSubHover(ch)}
                                    onClick={() => setOpenCat(null)}
                                    data-testid={`megamenu-sub-${ch.slug}`}>
                                    <span className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-400 group-hover:bg-green-50 group-hover:text-green-500"}`}>
                                        {ch.image_url ? <img src={ch.image_url} alt="" className="w-3.5 h-3.5 object-contain" /> : <ChildIcon size={11} />}
                                    </span>
                                    <span className={`font-medium truncate flex-1 ${isActive ? "text-green-700" : "text-gray-700 group-hover:text-green-700"}`}
                                        style={{ fontSize: "12px" }}>{ch.name}</span>
                                    {hasGC && <ChevronRight size={10} className="text-gray-300 flex-shrink-0" />}
                                </Link>
                            );
                        })}
                    </div>

                    {/* MIDDLE: Grandchildren + Brands */}
                    <div className="flex-1 px-5 min-w-0 overflow-y-auto" style={{ maxHeight: `${maxH - 32}px` }}>
                        {hoveredSub && shouldShowGrandchildren && subGrandchildren.length > 0 && (
                            <div className="mb-4">
                                <p className="text-gray-400 font-bold uppercase tracking-wider mb-2" style={{ fontSize: "10px" }}>{hoveredSub.name}</p>
                                <div className="grid grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-1">
                                    {subGrandchildren.map(gc => {
                                        const GcIcon = getIcon(gc.icon);
                                        return (
                                            <Link key={gc.id} to={`/kategorie/${gc.slug}`}
                                                className="flex items-center gap-2 py-1.5 hover:text-green-700 transition-colors text-gray-600 group"
                                                style={{ fontSize: "12px" }}
                                                onClick={() => setOpenCat(null)}>
                                                <GcIcon size={11} className="text-gray-400 group-hover:text-green-500 flex-shrink-0" />
                                                <span className="truncate">{gc.name}</span>
                                            </Link>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {shouldShowBrands && brands.length > 0 && (
                            <div className="mb-4">
                                <p className="text-gray-400 font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ fontSize: "10px" }}>
                                    <Users size={10} /> Znacky
                                </p>
                                <div className="grid grid-cols-3 xl:grid-cols-4 gap-2">
                                    {brands.map((b, idx) => {
                                        const bName = typeof b === "string" ? b : (b?.name || "");
                                        const bLogo = typeof b === "object" ? b?.logo_url : null;
                                        if (!bName) return null;
                                        return (
                                            <Link key={`${bName}-${idx}`} to={`/kategorie/${openCat.slug}?brand=${encodeURIComponent(bName)}`}
                                                className="flex items-center gap-2 px-2 py-1.5 rounded border border-gray-100 hover:border-green-200 hover:bg-green-50 transition-colors"
                                                onClick={() => setOpenCat(null)}
                                                data-testid={`megamenu-brand-${bName}`}>
                                                {bLogo ? (
                                                    <img src={bLogo} alt={bName} style={{ height: `${14 * brandIconScale}px`, maxWidth: `${28 * brandIconScale}px` }} className="w-auto object-contain" />
                                                ) : null}
                                                <span className="text-gray-700 font-medium truncate" style={{ fontSize: "11px" }}>{bName}</span>
                                            </Link>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {(!hoveredSub || subGrandchildren.length === 0) && brands.length === 0 && (
                            <div className="flex items-center justify-center h-full text-gray-300" style={{ fontSize: "12px" }}>
                                Najedte na podkategorii pro vice moznosti
                            </div>
                        )}
                    </div>

                    {/* RIGHT: Product Previews */}
                    {products.length > 0 && (
                        <div className="flex-shrink-0 border-l border-gray-100 pl-4 overflow-y-auto" style={{ width: "280px", maxHeight: `${maxH - 32}px` }}>
                            <p className="text-gray-400 font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ fontSize: "10px" }}>
                                <Star size={10} /> {hoveredSub ? hoveredSub.name : openCat.name} - top produkty
                            </p>
                            <div className="space-y-1">
                                {products.map(p => (
                                    <Link key={p.id} to={`/produkt/${p.slug}`}
                                        className="flex items-center gap-3 py-2 hover:bg-green-50 rounded-lg px-2 transition-colors group"
                                        onClick={() => setOpenCat(null)}
                                        data-testid={`megamenu-product-${p.slug}`}>
                                        <div className="w-14 h-14 rounded-lg bg-gray-50 border border-gray-100 flex-shrink-0 overflow-hidden flex items-center justify-center">
                                            {p.main_image ? (
                                                <img src={p.main_image} alt={p.name} className="w-full h-full object-contain p-0.5" loading="lazy" />
                                            ) : (
                                                <Package size={16} className="text-gray-300" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-gray-700 group-hover:text-green-700 font-medium leading-tight line-clamp-2" style={{ fontSize: "11px" }}>{p.name}</p>
                                            <p className="font-bold mt-0.5" style={{ fontSize: "13px", color: "var(--sg-primary)" }}>
                                                {(p.sale_price || p.price || 0).toLocaleString("cs-CZ")} Kc
                                                {p.sale_price && p.price > p.sale_price && (
                                                    <span className="text-gray-400 line-through font-normal ml-1" style={{ fontSize: "10px" }}>{p.price.toLocaleString("cs-CZ")} Kc</span>
                                                )}
                                            </p>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
